import { useEffect, useState } from "react";

import { HTTP_STATUS, useApiClient } from "../../api-client";
import { useEmployment } from "../home/useEmployment";
import { restoreCards } from "./restoreCards";

enum CardState {
  CANCELLED = "cancelled",
  SENT = "sent",
  DEACTIVATED = "deactivated",
  ACTIVE = "active",
  BLOCKED = "blocked",
  PENDING = "pending",
}
enum CardType {
  VIRTUAL = "virtual",
  PHYSICAL = "physical",
}

enum CardCategory {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  REPLACEMENT = "replacement",
  DISUSE = "disuse",
}

interface Card {
  cardId: string;
  requestUpdate?: boolean;
  type: CardType;
  state: CardState;
  categoryType: CardCategory;
  requestTimestamp: number;
  activatedTimestamp: number;
  isVirtual: boolean;
  isPhysical: boolean;
  isCancelled: boolean;
  expirationIsoDate: string;
}

const useCards = () => {
  const { get, post, put } = useApiClient();
  const [isLoading, setIsLoading] = useState(true);
  const [cards, setCards] = useState<Card[]>();
  const { employmentId } = useEmployment();

  const getCards = async () => {
    const { data, status } = await get(
      `/v2/employees/${employmentId}/employee-cards/`
    );

    if (status !== HTTP_STATUS.OK) {
      setIsLoading(false);

      return;
    }

    if (data) {
      const restoredCardsData = restoreCards(data.content);
      setCards(restoredCardsData);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (!employmentId) return;
    getCards();
  }, [employmentId]);

  const togglePhysicalCard = async (cardId: string) => {
    await post(`/v1/employees/toggle-card/${cardId}`);
  };

  const toggleVirtualCard = async (cardId: string, state: boolean) => {
    if (state) return await put(`/v1/employees/card/${cardId}/activation`);

    return await put(`/v1/employees/card/${cardId}/deactivation`);
  };

  return {
    cards,
    togglePhysicalCard,
    toggleVirtualCard,
    isLoading,
  };
};

export { useCards, CardState, CardType, CardCategory, type Card };
